import {
  TIntegracaoDisplayRules,
  TIntegracaoPorIdCampo
} from "../../../../services/CadastroDeIntegracaoGateway";
import {
  ETipoDado,
  ETipoVisualizacao,
  EValidacaoNomeada,
  VISUALIZATION_TYPE_BY_NAME
} from "../../../../@types/IntegracaoCampoEnum";
import {
  TIntegracaoCampoModel,
  TIntegracaoRegraExibicao
} from "../../../../@types/IntegracaoCampoModel";

const DEFAULT_GROUP = "Cadastro";

export default function useNormalizeFields() {
  const resolveVisualizationType = (
    field: TIntegracaoPorIdCampo
  ): ETipoVisualizacao => {
    return VISUALIZATION_TYPE_BY_NAME[field.tipo] ?? ETipoVisualizacao.TEXT;
  };

  const parseDisplayRules = (
    rules: TIntegracaoDisplayRules | null | undefined
  ): TIntegracaoRegraExibicao | null => {
    if (!rules || typeof rules !== "object") return null;

    const nomeCampo = rules.DependsOnFieldId;
    if (typeof nomeCampo !== "string" || !nomeCampo) return null;

    return {
      nomeCampo,
      operador: rules.Operator ?? "Equals",
      valores: Array.isArray(rules.Values) ? rules.Values : [],
      visivelQuandoSatisfaz: rules.VisibleWhenMatched ?? true,
      origem: rules.Source ?? "FieldValue",
      atributo: rules.Attribute ?? null,
      agregacao: rules.Aggregation ?? "Any"
    };
  };

  const parseNamedValidation = (
    field: TIntegracaoPorIdCampo
  ): EValidacaoNomeada | null => {
    const validation = field.validacao;
    const name =
      typeof validation === "string"
        ? validation
        : (validation?.campoId ?? validation?.CampoId ?? null);
    if (!name) return null;
    const names = Object.values(EValidacaoNomeada) as string[];

    return names.includes(name) ? (name as EValidacaoNomeada) : null;
  };

  const normalizeField = (
    field: TIntegracaoPorIdCampo
  ): TIntegracaoCampoModel => {
    const visualizationType = resolveVisualizationType(field);

    return {
      nomeCampo: field.campoId,
      label: field.label,
      tipo: visualizationType as unknown as ETipoDado,
      tipoVisualizacao: visualizationType,
      obrigatorio: field.obrigatorio,
      criptografado: field.criptografado,
      mascara: field.mascara,
      descricao: field.descricao,
      placeholder: field.placeholder,
      hint: field.hint ?? [],
      grupo: field.grupo?.trim() || DEFAULT_GROUP,
      tamanho: field.limiteCaracteres,
      fieldSize: field.fieldSize,
      ordem: field.ordemDeExibicao,
      valorPadrao: field.valorPadrao,
      valorPersistido:
        field.value === null || field.value === undefined
          ? undefined
          : Array.isArray(field.value)
            ? field.value
            : [field.value],
      hasPersistedValue: field.hasPersistedValue ?? false,
      visivel: field.visivel,
      desabilitado: field.desabilitado,
      opcoes: (field.opcoes ?? []).map((option) => ({
        value: option.valor,
        label: option.texto,
        categoria: option.categoria ?? null,
        attributes: option.attributes ?? null
      })),
      regraExibicao: parseDisplayRules(field.displayRules),
      regraObrigatoriedade: parseDisplayRules(field.requiredRules),
      minLength: field.minLength ?? null,
      validacaoNomeada: parseNamedValidation(field),
      endpoint: field.endpoint,
      endpointParameters: field.endpointParameters ?? null,
      nomeObjetoNoLegado: field.nomeObjetoNoLegado,
      payloadTransform: field.payloadTransform ?? null,
      includeInPayload: field.includeInPayload ?? true,
      action: field.action ?? null,
      remoteCollection: field.remoteCollection ?? null,
      refetchDefinitionParameter: field.refetchDefinitionParameter ?? null
    };
  };

  const normalizeFields = (
    fields: TIntegracaoPorIdCampo[]
  ): TIntegracaoCampoModel[] => {
    return [...fields]?.map(normalizeField)?.sort((a, b) => a.ordem - b.ordem);
  };

  const groupFields = (fields: TIntegracaoCampoModel[]) => {
    const groups = new Map<string, TIntegracaoCampoModel[]>();
    fields.forEach((field) => {
      const current = groups.get(field.grupo) ?? [];
      current.push(field);
      groups.set(field.grupo, current);
    });

    return Array.from(groups, ([grupo, campos]) => ({ grupo, campos }));
  };

  return {
    normalizeField,
    normalizeFields,
    groupFields
  };
}
